import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export interface VisibilityColumn {
  key: string;
  label: ReactNode;
}

// Shared "eye" column show/hide dropdown used by DataTable (server-side) and
// DataGrid (client-side). Markup mirrors the legacy finkit-datatable column
// toggle (btn-outline-primary + fa fa-eye, "Show/Hide Columns" header + reset,
// form-check rows).
export function ColumnVisibilityDropdown({
  columns,
  hidden,
  onToggle,
  onReset,
}: {
  columns: VisibilityColumn[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  onReset?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const idBase = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="dropdown column-toggle-wrapper" ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary dropdown-toggle"
        aria-expanded={open}
        title="Show / hide columns"
        onClick={() => setOpen((v) => !v)}
      >
        <i className="fa fa-eye" />
      </button>
      {open && (
        <ul
          className="dropdown-menu show p-2 text-nowrap"
          style={{ position: 'absolute', right: 0, minWidth: 200, maxHeight: 300, overflowY: 'auto', zIndex: 1056 }}
        >
          <li
            className="dropdown-header d-flex justify-content-between align-items-center pb-2 border-bottom mb-2"
            style={{ fontSize: 14, color: '#333', fontWeight: 'bold' }}
          >
            Show/Hide Columns
            {onReset && (
              <i
                className="fa fa-refresh text-primary ms-1"
                style={{ cursor: 'pointer' }}
                title="Reset / Select All"
                onClick={onReset}
              />
            )}
          </li>
          {columns.map((c) => {
            const id = `${idBase}-${c.key}`;
            return (
              <li key={c.key}>
                <div className="form-check">
                  <input
                    className="form-check-input column-toggle-checkbox"
                    type="checkbox"
                    id={id}
                    checked={!hidden.has(c.key)}
                    onChange={() => onToggle(c.key)}
                  />
                  <label className="form-check-label w-100" style={{ cursor: 'pointer' }} htmlFor={id}>
                    {c.label}
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
